import * as d3 from 'd3';
import { throttle } from 'lodash';
import { IntlShape } from 'react-intl';
import { BrushableChartController } from 'src/utils/helpers/BaseChartController';
import { v4 as uuid } from 'uuid';
import { GraphTooltipController } from '../util/GraphTooltipController';
import { JobGraphMask, JobPressureGraphDataElement, JobPressureGraphDataSegment } from '../util/types';

export class WaterPressureGraphController extends BrushableChartController {
  private readonly clipId = `clip-${uuid()}`;
  private readonly xMin: number = 0;
  private readonly yMin: number = 0;
  private xMax: number = 700;
  private readonly yMax: number = 200;
  private readonly x: d3.ScaleTime<number, number, never> = d3.scaleTime().range([this.xMin, this.xMax]);
  private readonly xAxis = d3.axisBottom(this.x);
  private readonly y: d3.ScaleLinear<number, number, never> = d3.scaleLinear().range([this.yMin, this.yMax]);
  private readonly yAxis = d3.axisLeft(this.y);
  private readonly tooltip: GraphTooltipController;

  /** The rectangle element that defines the clipping mask for the graph area. */
  private readonly clipRect: d3.Selection<SVGRectElement, unknown, null, unknown>;

  /** The group element that contains all graph content. */
  private readonly rootGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The group element that contains the x axis. */
  private readonly xAxisGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The group element that contains the y axis. */
  private readonly yAxisGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The group element that contains the graph "mask", which highlights portions of the graph. */
  private readonly maskGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The group element that contains the user selection. */
  private readonly selectionGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The group element that contains the brush selection. */
  private readonly brushGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The line element for the main graph data. */
  private readonly pressureLine: d3.Selection<SVGPathElement, JobPressureGraphDataSegment, null, unknown>;

  /** The external function that is invoked whenever zoom is performed. */
  private onZoom: (isModified: boolean) => void = () => {};

  /** The external function that is used to retrieve the tooltip text. */
  private getTooltip: (at: Date | number, item: JobPressureGraphDataElement) => string[] = () => [];

  /** Whether or not a selection item has been pinned. */
  private isSelectionPinned = false;

  /** The width of the svg. */
  private get width() {
    return this.xMax - this.xMin;
  }

  /** The height of the svg. */
  private get height() {
    return this.yMax - this.yMin;
  }

  /** The viewbox of the svg. */
  private get viewBox() {
    return {
      xMin: this.xMin,
      yMin: this.yMin,
      width: this.width,
      height: this.height,
      toString: function (this) {
        return `${this.xMin} ${this.yMin} ${this.width} ${this.height}`;
      },
    };
  }

  /** The full domain of the graph data along the x axis. */
  private get domain(): [number, number] {
    const data = this.pressureLine.datum();
    const result = d3.extent(data, d => d.timestamp);
    if (typeof result[0] !== 'number') return [0, 0];
    return result;
  }

  /** The computed height of the x axis. */
  private get xAxisHeight() {
    return this.xAxisGroup.node()?.getBBox().height ?? 0;
  }

  /** The computed width of the y axis. */
  private get yAxisWidth() {
    return this.yAxisGroup.node()?.getBBox().width ?? 0;
  }

  private readonly pressureLineMapper = d3
    .line<JobPressureGraphDataElement>()
    .x(d => this.x(d.timestamp))
    .y(d => this.y(d.pressure));

  constructor(svg: SVGSVGElement) {
    super(svg);
    this.svg.attr('viewBox', this.viewBox.toString());
    this.svg.selectChildren().remove();

    // Create clipPath for content area. Prevents overflow during pan/zoom
    this.clipRect = this.svg
      .append('defs')
      .append('svg:clipPath')
      .attr('id', this.clipId)
      .append<SVGRectElement>('svg:rect')
      .attr('data-id', 'clip-rect')
      .attr('x', this.viewBox.xMin)
      .attr('y', this.viewBox.yMin)
      .attr('width', this.viewBox.width)
      .attr('height', this.viewBox.height);

    // Create main content group. Will update with data in `setData`.
    this.rootGroup = this.svg
      .append('g')
      .attr('data-id', 'content')
      .attr('clip-path', `url(#${this.clipId})`)
      .append('g')
      .attr('data-id', 'root');

    // Create the main pressure line.
    this.pressureLine = this.rootGroup
      .append('path')
      .attr('data-id', 'pressure-line')
      .classed('water-pressure-graph-line', true)
      .datum<JobPressureGraphDataSegment>([]);

    // Create the group for masks over the content.
    this.maskGroup = this.rootGroup.append('g').attr('data-id', 'mask');

    // Create the group for brushing (allowing the user to select a region to zoom to).
    this.brushGroup = this.rootGroup
      .append('g')
      .classed('water-pressure-graph-brush', true)
      .attr('data-id', 'brush')
      .call(this.brush);

    // Create the group for the annotation markers.
    this.selectionGroup = this.rootGroup
      .append('g')
      .attr('data-id', 'selection')
      .classed('water-pressure-graph-selection', true);
    this.selectionGroup.append('line').attr('target', true).attr('y1', 0).attr('y2', this.viewBox.height);
    this.selectionGroup.append('circle').attr('target', true).attr('r', 8.5);

    // Create group for x/y axis.
    const axisGroup = this.svg.append('g').attr('data-id', 'axis');
    this.xAxisGroup = axisGroup
      .append('g')
      .classed('x-axis graph-timestamp graph-label', true)
      .attr('data-id', 'axis-x');
    this.yAxisGroup = axisGroup.append('g').classed('y-axis graph-label', true).attr('data-id', 'axis-y');

    this.tooltip = new GraphTooltipController(svg);

    // Bind all event listeners.
    this.brush.on('brush', this.handleBrushStart.bind(this));
    this.rootGroup
      .on('click', this.handleMouseClick.bind(this))
      .on('mouseover', this.handleMouseOver.bind(this))
      .on('mousemove', this.handleMouseMove.bind(this))
      .on('mouseout', this.handleMouseOut.bind(this));
  }

  public setDisplayConfiguration(intl: IntlShape, timezone: string, hideTimestamp: boolean) {
    if (hideTimestamp) {
      this.xAxis.ticks(0);
      this.xAxis.tickFormat(null);
    } else {
      const dateFormatter = new Intl.DateTimeFormat(intl.locale, {
        timeZone: timezone,
        month: 'short',
        day: 'numeric',
      });
      const timeFormatter = new Intl.DateTimeFormat(intl.locale, {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
      });

      this.xAxis.tickFormat(d => {
        const domain = this.xAxis.scale().domain();
        const span = domain[domain.length - 1].valueOf() - domain[0].valueOf();
        // If domain spans more than two days, use a date as the label instead of the time.
        if (span > 1000 * 60 * 60 * 24 * 2) {
          return dateFormatter.format(d.valueOf());
        } else {
          return timeFormatter.format(d.valueOf());
        }
      });
    }

    this.render();
  }

  public setTooltipAccessor(accessor: typeof this.getTooltip) {
    this.getTooltip = accessor;
  }

  public setOnZoom(onZoom: (isModified: boolean) => void) {
    this.onZoom = onZoom;
  }

  public setAspectRatio(aspectRatio: number) {
    this.xMax = Math.round(this.yMax * aspectRatio);
    this.svg.attr('viewBox', this.viewBox.toString());
    this.render();
    this.tooltip.onParentLayoutChanged(); // Inform tooltip controller that SVG has been resized.
  }

  public onResize(entries: ResizeObserverEntry[], observer: ResizeObserver) {
    if (entries.length === 0) return;
    this.render();
    this.tooltip.onParentLayoutChanged(); // Inform tooltip controller that SVG has been resized.
  }

  public setData(data: JobPressureGraphDataSegment, masks: JobGraphMask[] = []) {
    try {
      // Update line data
      this.pressureLine.datum(data);

      const maxY = d3.max(data, i => i.pressure);
      // Update Y axis
      this.y.domain([0, maxY ?? 10]);

      if (!this.brushSelection) {
        // Only update the domain if we don't have an active selection.
        this.x.domain(this.domain);
      }
      this.yAxisGroup.call(this.yAxis);
      this.xAxisGroup.call(this.xAxis);

      this.maskGroup.selectAll('rect').data(masks).join('rect');

      this.render();
    } catch (err) {
      console.error('Error setting data for water pressure graph.', err);
    }
  }

  public reset() {
    this.isSelectionPinned = false;
    this.setSelectionVisible(false);
    this.resetZoom(true);
  }

  protected async performZoom(e: d3.D3BrushEvent<number>): Promise<void> {
    const extent = e.selection as [number, number] | null;
    if (!extent) return;
    const xRange = this.getXAxisRange();
    this.onZoom(this.isZoomActive);

    const selectionX = this.x.invert(parseInt(this.selectionGroup.select('line').attr('x1')) || 0);
    const range = [Math.max(extent[0], xRange[0]), Math.min(extent[1], xRange[1])];
    this.x.domain([this.x.invert(range[0]), this.x.invert(range[1])]);

    this.xAxisGroup.transition('zoom').duration(500).call(this.xAxis);
    this.pressureLine.transition('zoom').attr('d', this.pressureLineMapper);

    if (this.isSelectionPinned) this.drawSelection(selectionX, 'zoom');
    this.brushGroup.call(this.brush.clear);
  }

  protected async performZoomReset(instant?: boolean | undefined): Promise<void> {
    this.onZoom(false);

    const selectionX = this.x.invert(parseInt(this.selectionGroup.select('line').attr('x1')) || 0);
    this.x.domain(this.domain);

    const duration = instant ? 0 : 500;
    this.xAxisGroup.transition('zoom').duration(duration).call(this.xAxis);
    this.pressureLine.transition('zoom').attr('d', this.pressureLineMapper);

    if (this.isSelectionPinned) this.drawSelection(selectionX, 'zoom');
  }

  /** Draws masks over the content area of the graph. */
  private drawMasks() {
    const xRange = this.getXAxisRange();
    const yRange = this.getYAxisRange();
    this.maskGroup
      .selectAll<SVGRectElement, JobGraphMask>('rect')
      .attr('class', d => `graph-mask graph-mask-${d.type}`)
      .attr('x', d => {
        switch (d.type) {
          default:
            return xRange[0];
        }
      })
      .attr('width', d => {
        switch (d.type) {
          default:
            return xRange[1] - xRange[0];
        }
      })
      .attr('y', d => {
        switch (d.type) {
          case 'safe-y':
            return this.y(d.yMax);
          default:
            return yRange[1];
        }
      })
      .attr('height', d => {
        switch (d.type) {
          case 'safe-y':
            return this.y(d.yMin) - this.y(d.yMax);
          default:
            return yRange[0] - yRange[1];
        }
      });
  }

  /** Draws the selection marker over the graph. */
  private drawSelection(at: Date, transition?: string) {
    const data = this.pressureLine.datum();
    const point = data.find(i => i.timestamp >= at.valueOf());
    if (!point) return;

    let line = this.selectionGroup.select('line');
    let circle = this.selectionGroup.select('circle');
    if (transition) {
      line = line.transition(transition) as any;
      circle = circle.transition(transition) as any;
    }
    const x = this.x(at);
    const y = this.y(point?.pressure ?? 0);
    line.attr('x1', x).attr('x2', x);
    circle.attr('cx', x).attr('cy', y);

    this.tooltip.setText(this.getTooltip(at, point), x, 0, transition);
  }

  /**
   * After display values in the graph have been updated (such as axis labels), we must
   * measure those labels and update the size of the graph area. This performs all transforms
   * necessary to resize the plotting area.
   */
  private readonly render = throttle(
    async () => {
      // Update the text size of graph elements.
      const pixelUnit = this.getPixelUnit();
      this.xAxisGroup.style('font-size', `${pixelUnit * 14}px`);
      this.yAxisGroup.style('font-size', `${pixelUnit * 14}px`);

      // Wait for any previously applied changes to render to the screen, so we can measure.
      await new Promise(res => window.requestAnimationFrame(res));
      const xRange = this.getXAxisRange();
      const yRange = this.getYAxisRange();

      // Determine the number of ticks to display along the y axis.
      const yHeight = yRange[0] - yRange[1];
      const yTicks = Math.max(Math.ceil(yHeight / pixelUnit / 30), 2);
      this.yAxis.ticks(yTicks);

      // Determine the number of ticks to display along the x axis.
      const xWidth = xRange[1] - xRange[0];
      const xTicks = Math.max(Math.ceil(xWidth / pixelUnit / 120), 2);
      // Only update the tick count if we have a tick formatter set.
      if (!!this.xAxis.tickFormat()) this.xAxis.ticks(xTicks);

      this.y.range(yRange);
      this.yAxisGroup.call(this.yAxis);
      this.x.range(xRange);
      this.xAxisGroup.call(this.xAxis);

      // Reposition each axis.
      this.xAxisGroup.attr('transform', `translate(0, ${yRange[0]})`);
      this.yAxisGroup.attr('transform', `translate(${xRange[0]}, 0)`);

      // Update the visible charting area in the graph. This clips out any line chart data
      // below or to the left of the axis (which happens on zoom).
      this.clipRect
        .attr('x', xRange[0])
        .attr('width', xWidth + 5)
        .attr('height', yHeight + 5);

      // Set the bounds of how far horizontally we can pan.
      const extent: [[number, number], [number, number]] = [
        [xRange[0], yRange[1]],
        [xRange[1], yRange[0]],
      ];
      this.brush.extent(extent);

      // Redraw the line to fit it inside the new graph area.
      this.pressureLine.attr('d', this.pressureLineMapper);

      // Redraw the masked areas
      this.drawMasks();
    },
    160,
    { leading: true, trailing: true },
  );

  /** The range of the x axis, derived from the computed width of the y axis. */
  private getXAxisRange(): [number, number] {
    return [this.yAxisWidth + 5, this.viewBox.width - 5];
  }

  /** The range of the y axis, derived from the computed height of the x axis. */
  private getYAxisRange(): [number, number] {
    return [this.viewBox.height - this.xAxisHeight, 5];
  }

  private setSelectionVisible(isVisible: boolean) {
    this.selectionGroup.classed('visible', isVisible);
    this.tooltip.setVisible(isVisible);
  }

  private handleBrushStart() {
    if (this.isSelectionPinned) return;
    this.setSelectionVisible(false);
  }

  private handleMouseClick() {
    this.isSelectionPinned = !this.isSelectionPinned;
    this.setSelectionVisible(this.isSelectionPinned);
  }

  private handleMouseOver() {
    if (this.isSelectionPinned) return;
    this.setSelectionVisible(true);
  }

  private handleMouseOut() {
    if (this.isSelectionPinned) return;
    this.setSelectionVisible(false);
  }

  private handleMouseMove(event: MouseEvent) {
    if (this.isSelectionPinned) return;
    const x0 = this.x.invert(d3.pointer(event, this.rootGroup.node())[0]);

    this.setSelectionVisible(true);
    this.drawSelection(x0);
  }
}
